import { ApiClient, ApiError, SyncEngine, type Me } from '@clim/client-core';
import { uuidv7 } from '@clim/domain';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { IndexedDbStorage } from './idb-storage';

export const API_URL = import.meta.env.VITE_API_URL ?? '';
export const DEFAULT_TENANT = import.meta.env.VITE_DEFAULT_TENANT ?? 'prestige-habitat';

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSet(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}

function deviceId(): string {
  let id = safeGet('clim.deviceId');
  if (!id) {
    id = `web-${uuidv7()}`;
    safeSet('clim.deviceId', id);
  }
  return id;
}

export interface Session {
  api: ApiClient;
  me: Me;
  engine: SyncEngine;
  /** true when started without network from the cached identity (read + queue only) */
  offlineStart: boolean;
}

interface SessionCtx {
  state: 'loading' | 'anonymous' | 'ready';
  session: Session | null;
  /** returns the second-step token when the account has two-step verification */
  login(tenant: string, email: string, password: string): Promise<{ mfaToken: string } | null>;
  loginMfa(tenant: string, mfaToken: string, code: string): Promise<void>;
  logout(): Promise<void>;
  refreshMe(): Promise<void>;
  /**
   * Session expirée : reconnexion du MÊME compte, sans rien effacer sur l'appareil (modifications et fichiers en
   * attente gardés, envoyés juste après). Renvoie le jeton de 2e étape si le compte a la double vérification.
   */
  relogin(password: string): Promise<{ mfaToken: string } | null>;
  reloginMfa(mfaToken: string, code: string): Promise<void>;
}

const Ctx = createContext<SessionCtx | null>(null);

function makeEngine(api: ApiClient, me: Me) {
  const storage = new IndexedDbStorage(`clim:${me.tenant.id}:${me.user.id}`);
  const engine = new SyncEngine({
    storage,
    blobs: storage.blobs,
    transport: api,
    deviceId: deviceId(),
    actor: {
      userId: me.user.id,
      displayName: `${me.user.firstName} ${me.user.lastName}`,
      dossierReadScope: me.permissions['dossier.read'],
      canValidateSav: !!me.permissions['sav.validate'],
    },
  });
  return engine;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionCtx['state']>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const api = useMemo(
    () => new ApiClient({ baseUrl: API_URL, mode: 'web', deviceId: deviceId(), onAuthLost: () => undefined }),
    [],
  );

  const open = useCallback(
    async (me: Me, offlineStart: boolean) => {
      const engine = makeEngine(api, me);
      await engine.init();
      engine.start(60_000);
      setSession({ api, me, engine, offlineStart });
      setState('ready');
    },
    [api],
  );

  useEffect(() => {
    (async () => {
      try {
        if (await api.resume()) {
          const me = await api.me();
          safeSet('clim.me', JSON.stringify(me));
          return open(me, false);
        }
      } catch (err) {
        if (!(err instanceof ApiError && err.isNetwork)) throw err;
      }
      // No network: reopen the last session read-only + outbox, if any.
      const cached = safeGet('clim.me');
      if (!navigator.onLine && cached) return open(JSON.parse(cached) as Me, true);
      setState('anonymous');
    })().catch(() => setState('anonymous'));
  }, [api, open]);

  useEffect(() => {
    const onOnline = () => session?.engine.sync();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [session]);

  const openAfterLogin = async () => {
    const me = await api.me();
    safeSet('clim.me', JSON.stringify(me));
    await open(me, false);
  };

  const afterRelogin = async () => {
    if (!session) return;
    const me = await api.me();
    if (me.user.id !== session.me.user.id) throw new Error('Reconnectez-vous avec le même compte');
    safeSet('clim.me', JSON.stringify(me));
    setSession({ ...session, me, offlineStart: false });
    void session.engine.sync();
  };

  const value: SessionCtx = {
    state,
    session,
    async login(tenant, email, password) {
      const mfa = await api.login({ tenant, email, password, platform: 'web', deviceName: navigator.userAgent.slice(0, 90) });
      safeSet('clim.tenant', tenant);
      if (mfa) return mfa;
      await openAfterLogin();
      return null;
    },
    async loginMfa(_tenant, mfaToken, code) {
      await api.loginMfa(mfaToken, code);
      await openAfterLogin();
    },
    async logout() {
      try {
        // shared computers: no customer data left behind
        await session?.engine.reset();
        await api.logout();
      } finally {
        safeSet('clim.me', null);
        setSession(null);
        setState('anonymous');
      }
    },
    async relogin(password) {
      if (!session) return null;
      const mfa = await api.login({
        tenant: session.me.tenant.slug,
        email: session.me.user.email,
        password,
        platform: 'web',
        deviceName: navigator.userAgent.slice(0, 90),
      });
      if (mfa) return mfa;
      await afterRelogin();
      return null;
    },
    async reloginMfa(mfaToken, code) {
      await api.loginMfa(mfaToken, code);
      await afterRelogin();
    },
    async refreshMe() {
      if (!session) return;
      const me = await api.me();
      safeSet('clim.me', JSON.stringify(me));
      setSession({ ...session, me });
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('SessionProvider missing');
  return ctx;
}

/** The ready session (only use under an authenticated route). */
export function useReady(): Session {
  const { session } = useSession();
  if (!session) throw new Error('not authenticated');
  return session;
}

/** Re-renders when local data or sync status changes. */
export function useEngine() {
  const { engine } = useReady();
  useSyncExternalStore(
    (cb) => engine.subscribe(cb),
    () => engine.version,
  );
  return engine;
}

export function lastTenant() {
  return safeGet('clim.tenant') ?? DEFAULT_TENANT;
}
